import { ApiFailure } from "@/components/data-display/api-failure";
import { LoadingState } from "@/components/ui/system-state";
import { classifyFailure } from "@/lib/api-failure";
import { Link, useParams } from "react-router-dom";
import TransferDetailView from "@/features/transfers/transfer-detail";
import { useTransferDetail } from "@/hooks/use-transfer-detail";
import { useWalletNames } from "@/hooks/use-wallet-names";

export default function TransferDetailPage() {
  const { id } = useParams();
  // ALL-5794: the page shows the transfer's real status only. The list's `?state=` link parameter
  // is ignored, so a URL can never make a transfer look further along than it is.
  const { detail, loading, error, refresh } = useTransferDetail(id!);
  const { byId } = useWalletNames();

  // The loading branch is the shared LoadingState (components/ui/system-state)
  // rather than a page-local skeleton stack, so the detail page announces the
  // same role/status label as every other loading surface in the app (F5).
  if (loading) {
    return <LoadingState />;
  }

  if (error && !detail && classifyFailure({ reasonCode: error }) !== "not_found") {
    return <ApiFailure error={error} subject="this transfer" onRetry={refresh} />;
  }

  if (error || !detail) {
    return (
      <div className="max-w-[760px]">
        <h1 className="text-xl font-bold text-ink">Transfer not found</h1>
        <p className="mt-2 text-sm text-muted">
          {`No transfer intent matches "${id}". It may have been removed, or you may not have access.`}
        </p>
        <Link to="/transfers" className="mt-4 inline-block text-[13px] font-semibold text-primary hover:underline">
          ← Back to Transfers
        </Link>
      </div>
    );
  }

  const walletId = detail.record?.transfer.wallet ?? "";
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={refresh}
          className="text-xs text-muted underline hover:text-ink"
        >
          Refresh
        </button>
      </div>
      <TransferDetailView detail={detail} sourceWalletName={byId.get(walletId)?.name} />
    </div>
  );
}
